import { formatFairPayStatusAmount } from "@components/FairPayStatusShort/formatFairPayStatusAmount";
import { normalizePeriodBarState } from "@components/PeriodBar/periodBarUtils";
import { resolvePayRangeVersionStatus } from "@components/PayRangeVersionRow/payRangeVersionRowUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

export const PAY_PLAN_LIFECYCLE_STATUS = {
  DRAFT: "draft",
  RELIEVED: "relieved",
  PROPOSED: "proposed",
  IN_FORCE: "in-force",
  TERMINATED: "terminated",
  VOIDED: "voided",
};

export const PAY_PLAN_PERIOD_STATE = {
  FUTURE: "future",
  CURRENT: "current",
  LAPSED: "lapsed",
  NONE: "none",
};

const VALID_LIFECYCLE = new Set(Object.values(PAY_PLAN_LIFECYCLE_STATUS));
const VALID_PERIOD_STATES = new Set(Object.values(PAY_PLAN_PERIOD_STATE));

export function normalizePayPlanLifecycleStatus(raw) {
  const key = String(raw || "")
    .toLowerCase()
    .trim()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");
  if (key === "inforce" || key === "in-force") return PAY_PLAN_LIFECYCLE_STATUS.IN_FORCE;
  return VALID_LIFECYCLE.has(key) ? key : PAY_PLAN_LIFECYCLE_STATUS.IN_FORCE;
}

export function normalizePayPlanPeriodState(raw) {
  const key = String(raw || "")
    .toLowerCase()
    .trim();
  if (key === "none" || key === "") return PAY_PLAN_PERIOD_STATE.NONE;
  return VALID_PERIOD_STATES.has(key) ? key : PAY_PLAN_PERIOD_STATE.LAPSED;
}

/**
 * Rule A-31: PP-1234.Vn, PP-1234.?, or ? when there is no plan number yet.
 */
export function formatPayPlanVersionCode({
  planNumber = null,
  versionNumber = null,
  status = PAY_PLAN_LIFECYCLE_STATUS.IN_FORCE,
  targetsExistingPlan = false,
} = {}) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED) {
    if (planNumber != null && String(planNumber).trim() !== "") {
      return `PP-${planNumber}.?`;
    }
    return "?";
  }

  const hasPlan = planNumber != null && String(planNumber).trim() !== "";
  const hasVersion = versionNumber != null && String(versionNumber).trim() !== "";
  if (!hasPlan) return "?";
  if (!hasVersion) return `PP-${planNumber}.V?`;
  return `PP-${planNumber}.V${versionNumber}`;
}

export function formatPayPlanAmount(amount, currencyCode = "USD", amountFormatted = "") {
  if (amountFormatted) return amountFormatted;
  return formatFairPayStatusAmount(amount, currencyCode);
}

export function formatPayPlanEffectiveText(
  effectiveFrom,
  effectiveTo,
  openEnded,
  prefix = "Pay plan effective from"
) {
  const fromLabel = formatFairPayDate(effectiveFrom);
  if (!fromLabel) return prefix;
  if (openEnded || effectiveTo == null || effectiveTo === "") {
    return `${prefix} ${fromLabel}`;
  }
  const toLabel = formatFairPayDate(effectiveTo);
  return toLabel ? `${prefix} ${fromLabel} through ${toLabel}` : `${prefix} ${fromLabel}`;
}

/** Map pay-range timeline status to PeriodBar-style state keys. */
export function resolvePayPlanPeriodState(effectiveFrom, effectiveTo, referenceDate) {
  const timelineStatus = resolvePayRangeVersionStatus(effectiveFrom, effectiveTo, referenceDate);
  if (timelineStatus === "future") return PAY_PLAN_PERIOD_STATE.FUTURE;
  if (timelineStatus === "current") return PAY_PLAN_PERIOD_STATE.CURRENT;
  return PAY_PLAN_PERIOD_STATE.LAPSED;
}

export function isValidPayPlanRowCombination(status, state, openEnded) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  const periodState = normalizePayPlanPeriodState(state);

  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.VOIDED) {
    return periodState === PAY_PLAN_PERIOD_STATE.NONE;
  }

  if (periodState === PAY_PLAN_PERIOD_STATE.NONE) return false;

  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.TERMINATED) {
    return (
      !openEnded &&
      (periodState === PAY_PLAN_PERIOD_STATE.CURRENT ||
        periodState === PAY_PLAN_PERIOD_STATE.LAPSED)
    );
  }

  if (periodState === PAY_PLAN_PERIOD_STATE.LAPSED) {
    return openEnded === false;
  }

  return true;
}

function normalizePayPlanApprovalStatus(raw) {
  const value = String(raw || "Draft").trim();
  if (value === "Pending approval" || value === "Approved" || value === "Rejected") {
    return value;
  }
  return "Draft";
}

/** B-19 fallback when PayPlanRowCard is used without explicit menuItems. */
export function buildPayPlanRowMenuItems(status, approvalStatus = "Draft") {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  const approval = normalizePayPlanApprovalStatus(approvalStatus);

  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED) {
    if (approval === "Draft") {
      return [
        { name: "Edit", value: "edit", icon: "icons/dropdown/edit-05.svg" },
        { name: "Submit", value: "submit", icon: "icons/dropdown/send-01.svg" },
        { name: "Delete", value: "delete", icon: "icons/dropdown/trash-01.svg", negative: true },
      ];
    }
    if (approval === "Pending approval") {
      return [
        {
          name: "Reset to draft",
          value: "reset_to_draft",
          icon: "icons/dropdown/reverse-left.svg",
        },
      ];
    }
    if (approval === "Rejected") {
      return [
        {
          name: "Void",
          value: "void",
          icon: "icons/dropdown/slash-circle-01.svg",
          negative: true,
        },
      ];
    }
    return [];
  }
  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.IN_FORCE) {
    return [
      { name: "Terminate", value: "terminate", icon: "icons/dropdown/stop-circle.svg" },
      { name: "Void", value: "void", icon: "icons/dropdown/slash-circle-01.svg", negative: true },
    ];
  }
  return [];
}

export function payPlanRowShowsMenu(status) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  return (
    lifecycle === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED ||
    lifecycle === PAY_PLAN_LIFECYCLE_STATUS.IN_FORCE
  );
}

export function payPlanRowSurfaceClass(status, state) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  const periodState = normalizePayPlanPeriodState(state);

  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED) {
    return "pay-plan-row-card--proposed";
  }
  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.VOIDED) {
    return "pay-plan-row-card--voided";
  }
  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.TERMINATED) {
    if (periodState === PAY_PLAN_PERIOD_STATE.LAPSED) {
      return "pay-plan-row-card--terminated-lapsed";
    }
    return "pay-plan-row-card--terminated-current";
  }

  if (periodState === PAY_PLAN_PERIOD_STATE.FUTURE) return "pay-plan-row-card--in-force-future";
  if (periodState === PAY_PLAN_PERIOD_STATE.CURRENT) return "pay-plan-row-card--in-force-current";
  return "pay-plan-row-card--in-force-lapsed";
}

export function payPlanRowUsesFilledStateBadge(status) {
  return normalizePayPlanLifecycleStatus(status) === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED;
}

export function payPlanRowIsCompact(status) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  return lifecycle === PAY_PLAN_LIFECYCLE_STATUS.TERMINATED || lifecycle === PAY_PLAN_LIFECYCLE_STATUS.VOIDED;
}

export function payPlanRowIsVoided(status) {
  return normalizePayPlanLifecycleStatus(status) === PAY_PLAN_LIFECYCLE_STATUS.VOIDED;
}

export function payPlanRowShowsStateBadge(status, state) {
  if (payPlanRowIsVoided(status)) return false;
  return normalizePayPlanPeriodState(state) !== PAY_PLAN_PERIOD_STATE.NONE;
}

/** PeriodBar state string for aria / interaction parity. */
export function payPlanRowPeriodBarState(status, state) {
  const lifecycle = normalizePayPlanLifecycleStatus(status);
  if (lifecycle === PAY_PLAN_LIFECYCLE_STATUS.PROPOSED) return "draft";
  const periodState = normalizePayPlanPeriodState(state);
  return normalizePeriodBarState(periodState);
}

export const PAY_PLAN_ROW_VALID_VARIANTS = [
  { status: "proposed", state: "future", openEnded: true },
  { status: "proposed", state: "future", openEnded: false },
  { status: "proposed", state: "current", openEnded: true },
  { status: "proposed", state: "current", openEnded: false },
  { status: "proposed", state: "lapsed", openEnded: false },
  { status: "in-force", state: "future", openEnded: true },
  { status: "in-force", state: "future", openEnded: false },
  { status: "in-force", state: "current", openEnded: true },
  { status: "in-force", state: "current", openEnded: false },
  { status: "in-force", state: "lapsed", openEnded: false },
  { status: "terminated", state: "current", openEnded: false },
  { status: "terminated", state: "lapsed", openEnded: false },
  { status: "voided", state: "none", openEnded: true },
  { status: "voided", state: "none", openEnded: false },
];

export const PAY_PLAN_ROW_INTERACTIONS = ["default", "hover", "selected", "focus"];
